import { useCotizador } from "context/apiContext";
import { TextsContext } from "context/textsContext";
import { useSteps } from "hooks/useSteps";
import useFormValidation from "./useFormValidation";
import { useContext, useMemo } from "react";
import { 
    clearErrorIfValid,
    createFieldErrorStructure,
    createNestedErrorStructure,
    getAge,
    getAgeInDays,
    getDiffDays,
    setErrorMessage,
    validateEmailFormat,
    validatePersonName
} from "helpers/toolsHelper";
import { 
    DEPENDENT_FIELDS,
    DEPENDENT_RELATIONSHIPS,
    ERROR_TYPES,
    MAIN_APPLICANT_FIELDS,
    VALIDATION_CONSTRAINTS
} from "constants/validationConstants";

const useFormValidateSteps = () => {
    const { texts } = useContext(TextsContext);
    const { errors, contextState, dates } = useCotizador();
    const { getCurrentStepData } = useSteps();
    const { validateBirthdayAge } = useFormValidation();

    const stepFieldConfigs = useMemo(() => ({
        mainApplicant: MAIN_APPLICANT_FIELDS,
        dependent: DEPENDENT_FIELDS
    }), []);

    const validateMainApplicantData = () => {
        const validationErrors = {}

        const nameValidationMessage = texts?.general_field_validation_name ?? 'Name should not contain numbers';
        const lastNameValidationMessage = texts?.general_field_validation_last_name ?? 'Last name should not contain numbers';
        const emailValidationMessage = texts?.general_field_validation_email ?? 'Please introduce valid email';
        const coverageValidationMessage = texts?.general_field_coverage ?? 
          'Coverage must start either 15 before or 45 days after today';

        const dependentTableErrors = createNestedErrorStructure(errors, 'dependantsTable', 'invalid');
        const shouldShowDependentError = contextState?.dependants.length === 0 && contextState?.addDependant;

        setErrorMessage(dependentTableErrors.dependantsTable, ERROR_TYPES.INVALID, 
            shouldShowDependentError ? '[hidden]' : null);

        Object.assign(validationErrors, dependentTableErrors);

        if (getCurrentStepData?.()?.step !== 1) return validationErrors;
    
        stepFieldConfigs.mainApplicant.forEach((fieldKey) => {
            if (validationErrors[fieldKey] === undefined) {
                validationErrors[fieldKey] = errors[fieldKey] ?? {};
            }

            const fieldErrors = createFieldErrorStructure(errors, fieldKey);
            Object.assign(validationErrors, fieldErrors);
            
            clearErrorIfValid(
                validationErrors[fieldKey], 
                ERROR_TYPES.EMPTY, 
                !!contextState[fieldKey]
            );
        })

        if (contextState.name) {
            setErrorMessage(
                validationErrors.name, 
                ERROR_TYPES.INVALID,
                validatePersonName(contextState.name) ? null : nameValidationMessage
            );
        }

        if (contextState.surname) {
            setErrorMessage(
                validationErrors.surname, 
                ERROR_TYPES.INVALID,
                validatePersonName(contextState.surname) ? null : lastNameValidationMessage
            );
        }

        if (contextState.email) {
            setErrorMessage(
                validationErrors.email,
                ERROR_TYPES.INVALID,
                validateEmailFormat(contextState.email) ? null : emailValidationMessage
            );
        }

        setErrorMessage(
            validationErrors.birthday,
            ERROR_TYPES.INVALID,
            validateBirthdayAge(contextState.birthday, contextState.coverage)
        );

        if (dates.coverage) {
            const daysDifference = getDiffDays(new Date(), dates.coverage);

            const isDateInValidRange = daysDifference <= VALIDATION_CONSTRAINTS.COVERAGE_DATE_RANGE.MAX_DAYS_AFTER &&
                daysDifference >= VALIDATION_CONSTRAINTS.COVERAGE_DATE_RANGE.MIN_DAYS_BEFORE;
            
            setErrorMessage(
                validationErrors.coverage,
                ERROR_TYPES.INVALID,
                isDateInValidRange ? null : coverageValidationMessage
            );
        }

        return validationErrors;
    }

    const validateDependentData = () => {
        const validationErrors = createNestedErrorStructure({}, 'dependants', '');
        const currentDependentErrors = errors?.dependants ?? {};
        const dependentData = contextState?.dependantData;

        const dependentAgeMessage = texts?.general_field_validation_dependent_age ?? 
          'The age of the dependent cannot be older than 23 years.';
        const dependentDaysMessage = texts?.general_field_validation_dependent_days ?? 
          'The dependant should be at least 30 days old.';
        const spouseValidationMessage = texts?.general_field_validation_spouse ?? 
          'You can only select one spouse';
        const nameValidationMessage = texts?.general_field_validation_name ?? 'Name should not contain numbers';
        const lastNameValidationMessage = texts?.general_field_validation_last_name ?? 'Last name should not contain numbers';


        stepFieldConfigs.dependent.forEach((fieldKey) => {
            validationErrors.dependants[fieldKey] = currentDependentErrors[fieldKey] ?? {};
        
            clearErrorIfValid(
                validationErrors.dependants[fieldKey],
                ERROR_TYPES.EMPTY,
                !!dependentData?.[fieldKey]
            );
        })

        if (!dependentData) return validationErrors;

        const dependentAge = getAge(dependentData.birthday);
        const dependentAgeInDays = getAgeInDays(dependentData.birthday);
        const isChild = dependentData.componentId === DEPENDENT_RELATIONSHIPS.CHILD;
        const isOther = dependentData.componentId === DEPENDENT_RELATIONSHIPS.OTHER;
        const isSpouse = dependentData.componentId === DEPENDENT_RELATIONSHIPS.SPOUSE;

        const childTooOld = dependentData.birthday && dependentAge > VALIDATION_CONSTRAINTS.MAX_CHILD_AGE && isChild;

        setErrorMessage(
            validationErrors.dependants.birthday,
            ERROR_TYPES.INVALID_INPUT_SON,
            childTooOld ? dependentAgeMessage : null
        );

        const otherTooOld = dependentData.birthday && dependentAge > VALIDATION_CONSTRAINTS.MAX_CHILD_AGE && isOther;
        
        setErrorMessage(
            validationErrors.dependants.birthday,
            ERROR_TYPES.INVALID_INPUT_OTHER,
            otherTooOld ? dependentAgeMessage : null
        );

        const tooYoungInDays = dependentData.birthday && 
          dependentAgeInDays < VALIDATION_CONSTRAINTS.MIN_DEPENDENT_AGE_DAYS && 
          (isChild || isOther);

        if (tooYoungInDays) {
            setErrorMessage(
            validationErrors.dependants.birthday,
            ERROR_TYPES.INVALID_INPUT_OTHER,
            dependentDaysMessage
            );
        }

        if (isSpouse) {
            setErrorMessage(
                validationErrors.dependants.birthday,
                ERROR_TYPES.INVALID_INPUT_SPOUSE,
                validateBirthdayAge(dependentData.birthday, contextState.coverage, dependentData.componentId)
            );
        } else {
            setErrorMessage(
                validationErrors.dependants.birthday,
                ERROR_TYPES.INVALID_INPUT_SPOUSE,
                null
            );
        }

        if (contextState.dependantData.name) {
            setErrorMessage(
                validationErrors.dependants.name,
                ERROR_TYPES.INVALID,
                validatePersonName(dependentData.name) ? null : nameValidationMessage
            );
        }

        if (dependentData.surname) {
            setErrorMessage(
                validationErrors.dependants.surname,
                ERROR_TYPES.INVALID,
                validatePersonName(dependentData.surname) ? null : lastNameValidationMessage
            );
        }

        if (contextState.dependantData.componentId) {
            const existingSpouse = contextState.dependants.find(dep => 
              dep.componentId === DEPENDENT_RELATIONSHIPS.SPOUSE
            )

            const isAddingDuplicateSpouse = existingSpouse && 
              dependentData?.id !== existingSpouse.id && 
              isSpouse;

            setErrorMessage(
                validationErrors.dependants.componentId,
                ERROR_TYPES.INVALID,
                isAddingDuplicateSpouse ? spouseValidationMessage : null
            );
        }

        return validationErrors;
    }

    return { validateMainApplicantData, validateDependentData }
}

export default useFormValidateSteps;